import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { BsInfoCircle } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, HStack, Icon, IconButton, Switch, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import dataTypes from '../../../../../../../../../../../../constants/dataTypes';
import { fieldInputTypes } from '../../../../../../../../../../../../constants/fieldInputTypes';
import { inAppText } from '../../../../../../../../../../../../constants/inAppText';
import colors from '../../../../../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType
  from '../../../../../../../../../../../../types/RequirementTemplateBlueprintType';
import DropdownMenu from '../../../../../../../../../../../DropdownMenu';
import { findSelected } from '../../../../../../../../../../../DropdownMenu/util';
import FormInput from '../../../../../../../../../../../form/FormInput';
import InputLabel from '../../../../../../../../../../../form/InputLabel';
import {
  customPolicyPropertiesStack,
  requiredCustomPolicyStack
} from '../../../../../../../../../../util/customPolicyFormStacks';
import { customPolicyTypePropertyOptions } from '../../../../../../../../../../util/options';

import RequiredCoverageFieldTypeInputs from './RequiredCoverageFieldTypeInputs';

const CustomPolicyTypeForm = ({ formId, onSubmit, blueprint }) => {
  const dispatch = useDispatch();
  const currentCustomPolicyPropertyType = useSelector(blueprint.selectCurrentCustomPolicyPropertyFieldType);
  const selectedPropertyFieldTypes = useSelector(blueprint.selectSelectedPolicyPropertyFieldTypes);
  const currentEditCustomPolicyId = useSelector(blueprint.selectCurrentEditCustomPolicyId);
  const insurancePolicyTypes = useSelector(blueprint.selectInsurancePolicyTypes);

  useEffect(() => {
    if (currentEditCustomPolicyId) {
      dispatch(blueprint.setupEditCustomPolicyType(currentEditCustomPolicyId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePropertyTypeSelect = selected => {
    const propertyFieldType = customPolicyTypePropertyOptions.find(t => t.id === selected.value);

    if (selectedPropertyFieldTypes?.includes(propertyFieldType.id)) {
      return;
    }

    dispatch(blueprint.setCurrentPolicyPropertyFieldType(propertyFieldType));
    switch (propertyFieldType.id) {
      case fieldInputTypes.Checkboxes:
        dispatch(blueprint.updateCurrentPropertyInputOptions(
          propertyFieldType.id,
          [customPolicyPropertiesStack[propertyFieldType.id]]
        ));

        break;
      case fieldInputTypes.Monetary:
        dispatch(blueprint.updateCurrentPropertyInputOptions(
          propertyFieldType.id,
          [customPolicyPropertiesStack[propertyFieldType.id]]
        ));

        break;
      case fieldInputTypes.AlphaNumeric:
        dispatch(blueprint.updateCurrentPropertyInputOptions(
          propertyFieldType.id,
          [customPolicyPropertiesStack[propertyFieldType.id]]
        ));

        break;
      default:
        break;
    }
  };

  const getEditPolicyFormDefaultValues = () => {
    const policyTypeData = insurancePolicyTypes
      ?.data?.filter(policyType => policyType.id === parseInt(currentEditCustomPolicyId, 10));

    const properties = policyTypeData?.[0]?.properties;

    const booleanProperties = properties
      ?.filter(property => property.dataType === dataTypes.Boolean);
    const monetaryProperties = properties
      ?.filter(property => property.dataType === dataTypes.Monetary);
    const alphaNumericProperties = properties
      ?.filter(property => property.dataType === dataTypes.String);

    let defaultPropertyValues = {};
    booleanProperties.forEach((property, idx) => {
      defaultPropertyValues = Object.assign(
        defaultPropertyValues,
        { [`${property.dataType}.${idx}.label`]: property.displayName }
      );
    });

    monetaryProperties.forEach((property, idx) => {
      defaultPropertyValues = Object.assign(
        defaultPropertyValues,
        { [`${property.dataType}.${idx}.label`]: property.displayName }
      );
    });

    alphaNumericProperties.forEach((property, idx) => {
      const dataType = fieldInputTypes.AlphaNumeric;
      defaultPropertyValues = Object.assign(
        defaultPropertyValues,
        { [`${dataType}.${idx}.label`]: property.displayName }
      );
    });

    return {
      policyName: policyTypeData?.[0]?.displayName,
      description: policyTypeData?.[0]?.description,
      ...defaultPropertyValues,
      addToList: true
    };
  };

  const useFormMethods = useForm(
    {
      defaultValues: currentEditCustomPolicyId
        ? getEditPolicyFormDefaultValues()
        : { addToList: true }
    }
  );

  const { handleSubmit, watch, setValue, setError } = useFormMethods;
  const options = customPolicyTypePropertyOptions.filter(option => !selectedPropertyFieldTypes.includes(option.id));

  return (
    <form id={formId} onSubmit={handleSubmit(data => onSubmit(data, setError))}>
      <Box pb={2}>
        {requiredCustomPolicyStack.map(input => (
          <FormInput
            caption={input?.caption}
            inputName={input.name}
            key={input.name}
            labelName={input.label}
            required={input?.required}
            toolTip={input?.toolTip}
            type={input?.type}
            useFormMethods={useFormMethods}
            validationRules={input?.validationRules}
          />
        ))}
        <Box maxH="450px" mt={4}>
          <InputLabel labelName="Required Coverage" mb={selectedPropertyFieldTypes.length ? 4 : 2} />
          {!selectedPropertyFieldTypes.length && (
            <Text color={colors.greyScale['500']} mb={2} textStyle="bodySm">
              {inAppText.tooltips.templates.templateBuilder.createCustomPolicy.requiredCoverageHint}
            </Text>
          )}
        </Box>
        <RequiredCoverageFieldTypeInputs blueprint={blueprint} mt={4} useFormMethods={useFormMethods} />
        <Box height="90px" mt="26px">
          {options.length ? (
            <DropdownMenu
              labelKey="displayName"
              options={options}
              placeholder="Field Type"
              selected={currentCustomPolicyPropertyType?.id
                ? findSelected(customPolicyTypePropertyOptions, currentCustomPolicyPropertyType?.id).displayName : ''}
              width="400px"
              onSelect={handlePropertyTypeSelect}
            />
          ) : <></>}

          {!currentEditCustomPolicyId && (
          <Flex justify="space-between" mt="26px">
            <HStack flexFlow="row nowrap" w="85%">
              <Text
                color={colors.greyScale['900']}
                textStyle="bodyMd500"
              >
                Save Custom Policy Type to List
              </Text>
              <Tooltip
                hasArrow
                label={inAppText.tooltips.templates.templateBuilder.createCustomPolicy.saveToListToggle}
              >
                <IconButton
                  aria-label="User Help"
                  boxSize={1}
                  color={colors.greyScale['900']}
                  icon={<Icon as={BsInfoCircle} ml={-4} />}
                  variant="textGrey"
                />
              </Tooltip>
            </HStack>
            <Box alignItems="flex-end" pr="10px" textAlign="right" w="10%">
              <Switch
                isChecked={watch('addToList')}
                name="addToList"
                size="md"
                width="40px"
                onChange={e => setValue('addToList', e.target.checked)}
              />
            </Box>
          </Flex>
          )}
        </Box>
      </Box>
    </form>
  );
};

CustomPolicyTypeForm.propTypes = {
  formId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  blueprint: RequirementTemplateBlueprintType
};

export default CustomPolicyTypeForm;
